import type { ReactElement } from "react";
import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { planIds, type PlanId } from "@coachbrain/contracts";
import { getApi } from "./api";
import { setSession } from "./lib/session";
import { OverviewRoute } from "./routes/overview";
import { CaptureRoute, CheckRoute } from "./routes/capture";
import { ReviewRoute, ReviewsListRoute } from "./routes/review";
import { HistoryRoute } from "./routes/history";
import { ChallengesRoute } from "./routes/challenges";
import { ReportsRoute } from "./routes/reports";
import { ClientsRoute, LibraryRoute, LogPickerRoute, TodayRoute } from "./routes/clients";
import { SettingsRoute } from "./routes/settings";
import { GoogleFinishRoute, LoginRoute, OnboardingRoute, SignupRoute } from "./routes/auth";
import { PlanEditRoute } from "./routes/plan-edit";
import { TraineeCoachRoute, TraineeHomeRoute, TraineeReportRoute, TraineeReportsRoute, TraineeSessionRoute, TraineeSessionsRoute } from "./routes/t/home";
import { AdminAuditRoute, AdminChannelsRoute, AdminFlagsRoute, AdminKnowledgeRoute, AdminMethodsRoute, AdminPracticesRoute, AdminProvidersRoute, AdminSupportRoute } from "./routes/admin/index";

const root = createRootRoute({ component: () => <Outlet /> });

/** Only a relative, same-origin path is a valid return target (no open redirects). */
export function safeNext(next: unknown, fallback = "/clients"): string {
  return typeof next === "string" && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

/** A `?plan=` hint (marketing CTA) is only a known plan id; anything else is dropped silently. */
function planOf(v: unknown): PlanId | undefined {
  return typeof v === "string" && (planIds as readonly string[]).includes(v) ? (v as PlanId) : undefined;
}

/** Route guard: an unauthenticated visit to a protected route goes to /login and comes back afterwards. */
async function requireSignIn({ location }: { location: { href: string } }) {
  const api = await getApi();
  const me = await api.me().catch(() => null);
  setSession(me);
  if (!me) throw redirect({ to: "/login", search: { next: location.href } });
}

const r = <P extends string>(path: P, component: () => ReactElement) => createRoute({ getParentRoute: () => root, path, component, beforeLoad: requireSignIn });

const routes = [
  createRoute({ getParentRoute: () => root, path: "/", beforeLoad: () => { throw redirect({ to: "/clients" }); } }),
  createRoute({ getParentRoute: () => root, path: "/login", component: LoginRoute, validateSearch: (search: Record<string, unknown>): { next?: string; error?: string } => {
    const out: { next?: string; error?: string } = {};
    if (typeof search["next"] === "string") out.next = search["next"];
    if (typeof search["error"] === "string") out.error = search["error"];
    return out;
  } }),
  createRoute({ getParentRoute: () => root, path: "/signup", component: SignupRoute, validateSearch: (search: Record<string, unknown>): { plan?: PlanId } => { const plan = planOf(search["plan"]); return plan ? { plan } : {}; } }),
  createRoute({ getParentRoute: () => root, path: "/signup/google", component: GoogleFinishRoute, validateSearch: (search: Record<string, unknown>): { ticket?: string } => (typeof search["ticket"] === "string" && search["ticket"] ? { ticket: search["ticket"] } : {}) }),
  r("/onboarding", OnboardingRoute),
  r("/clients", ClientsRoute), r("/today", TodayRoute), r("/log", LogPickerRoute), r("/library", LibraryRoute),
  createRoute({ getParentRoute: () => root, path: "/settings", component: SettingsRoute, beforeLoad: requireSignIn, validateSearch: (search: Record<string, unknown>): { plan?: PlanId; billing?: "success" | "cancelled" } => {
    const out: { plan?: PlanId; billing?: "success" | "cancelled" } = {};
    const plan = planOf(search["plan"]);
    if (plan) out.plan = plan;
    if (search["billing"] === "success" || search["billing"] === "cancelled") out.billing = search["billing"];
    return out;
  } }),
  r("/clients/$id", OverviewRoute), r("/clients/$id/log", CaptureRoute), r("/clients/$id/check/$captureId", CheckRoute),
  r("/clients/$id/review/$reviewId", ReviewRoute), r("/clients/$id/reviews", ReviewsListRoute), r("/clients/$id/history", HistoryRoute),
  r("/clients/$id/challenges", ChallengesRoute), r("/clients/$id/reports", ReportsRoute), r("/clients/$id/plan/$planId/edit", PlanEditRoute),
  r("/t/home", TraineeHomeRoute), r("/t/reports", TraineeReportsRoute), r("/t/reports/$id", TraineeReportRoute), r("/t/sessions", TraineeSessionsRoute), r("/t/sessions/$id", TraineeSessionRoute), r("/t/coach", TraineeCoachRoute),
  r("/admin/practices", AdminPracticesRoute), r("/admin/flags", AdminFlagsRoute), r("/admin/methods", AdminMethodsRoute), r("/admin/knowledge", AdminKnowledgeRoute),
  r("/admin/providers", AdminProvidersRoute), r("/admin/channels", AdminChannelsRoute), r("/admin/audit", AdminAuditRoute), r("/admin/support", AdminSupportRoute),
] as const;

export const router = createRouter({ routeTree: root.addChildren(routes), defaultPreload: "intent" });
declare module "@tanstack/react-router" { interface Register { router: typeof router } }
